// Excel / CSV export straight from a report model (the same model the screen and PDF use).
import * as XLSX from 'xlsx'

const cellText = c => {
  if (c === null || c === undefined) return ''
  if (typeof c !== 'object') return c
  return [c.text, c.note].filter(Boolean).join(' | ')
}
const stripHtml = s => String(s).replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')

// Numbers stay numbers in Excel ("1,234.50" -> 1234.5); everything else as text
const excelValue = v => {
  if (typeof v === 'number') return v
  const s = String(v ?? '')
  return /^-?[\d,]+(\.\d+)?$/.test(s) ? Number(s.replace(/,/g, '')) : s
}

// Exported files may be handed to employees: drop screen-only formula/rate notes.
// Mirrors backend services/reports/reportCommon.js forExport().
function stripSections(sections) {
  return (sections || []).filter(s => !s.internal).map(s => {
    if (s.kind === 'notes') return { ...s, items: s.items.filter(i => !(i && i.internal)).map(i => (typeof i === 'string' ? i : i.html)) }
    if (s.kind === 'grid') return { ...s, items: stripSections(s.items) }
    if (s.kind === 'group') return { ...s, sections: stripSections(s.sections) }
    return s
  })
}
export function forExport(model) {
  const { internalFooter, internalSubtitle, ...rest } = model
  return { ...rest, sections: stripSections(model.sections) }
}

function collectTables(sections, prefix = '') {
  const out = []
  for (const s of sections) {
    if (s.kind === 'table') out.push({ ...s, sheetTitle: prefix ? `${prefix} - ${s.title || ''}` : s.title || 'Table' })
    else if (s.kind === 'grid') out.push(...collectTables(s.items, prefix))
    else if (s.kind === 'group') out.push(...collectTables(s.sections, s.title))
  }
  return out
}

function tableAoa(t) {
  const aoa = [t.columns.map(c => c.label)]
  for (const r of t.rows || []) aoa.push(t.columns.map(c => excelValue(cellText(r[c.key]))))
  if (t.footer) aoa.push(t.columns.map(c => excelValue(cellText(t.footer[c.key]))))
  return aoa
}

function sheetName(title, used) {
  let base = String(title || 'Sheet').replace(/[\[\]:*?/\\]/g, ' ').trim().slice(0, 28) || 'Sheet'
  let name = base
  let i = 2
  while (used.has(name)) name = `${base.slice(0, 25)} ${i++}`
  used.add(name)
  return name
}

export function exportModelToExcel(fullModel) {
  const model = forExport(fullModel)
  const wb = XLSX.utils.book_new()
  if (model.dir === 'rtl') wb.Workbook = { Views: [{ RTL: true }] }
  const used = new Set()

  const summary = [[model.title], ...model.subtitle.map(s => [s.replace(/[⁨⁩]/g, '')]), []]
  for (const s of model.sections) {
    if (s.kind === 'meta') { s.items.forEach(i => summary.push([i.label, excelValue(i.value)])); summary.push([]) }
    if (s.kind === 'cards') { s.cards.forEach(c => summary.push([c.label, excelValue(c.value), c.sub || ''])); summary.push([]) }
    if (s.kind === 'notes' && s.items.length) { summary.push([s.title || '']); s.items.forEach(n => summary.push([stripHtml(n)])); summary.push([]) }
  }
  if (model.footer) summary.push([model.footer])
  const ws0 = XLSX.utils.aoa_to_sheet(summary)
  ws0['!cols'] = [{ wch: 34 }, { wch: 18 }, { wch: 60 }]
  XLSX.utils.book_append_sheet(wb, ws0, sheetName(model.dir === 'rtl' ? 'الملخص' : 'Summary', used))

  for (const t of collectTables(model.sections)) {
    const aoa = tableAoa(t)
    const ws = XLSX.utils.aoa_to_sheet(aoa)
    ws['!cols'] = t.columns.map((c, i) => ({ wch: Math.min(60, Math.max(10, ...aoa.map(r => String(r[i] ?? '').length + 2))) }))
    XLSX.utils.book_append_sheet(wb, ws, sheetName(t.sheetTitle, used))
  }
  XLSX.writeFile(wb, `${model.filename || 'report'}.xlsx`)
}

export function exportModelToCSV(fullModel) {
  const model = forExport(fullModel)
  const tables = collectTables(model.sections)
  const t = tables.find(x => x.primary) || tables[0]
  if (!t) throw new Error('No table to export')
  const esc = v => {
    const s = String(v ?? '')
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const csv = tableAoa(t).map(r => r.map(esc).join(',')).join('\n')
  // BOM so Excel opens Arabic text correctly
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${model.filename || 'report'}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}
